import type { DateRange } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

const ID_ARRAY_SCHEMA = z.array(z.number().int());

export interface PlayoutFilterParams {
  eventTypeId: number | null;
  dateRange: DateRange<DateTime>;
  eventIds: number[] | null;
  deviceGroupIds: number[] | null;
  combineByName: boolean;
}

export const DEFAULT_PLAYOUT_FILTERS: PlayoutFilterParams = {
  eventTypeId: null,
  dateRange: [null, null],
  eventIds: null,
  deviceGroupIds: null,
  combineByName: true,
};

const parseIntParam = (params: URLSearchParams, key: string) => {
  const value = Number(params.get(key));
  return Number.isInteger(value) && value > 0 ? value : null;
};

const parseDateParam = (params: URLSearchParams, key: string) => {
  const raw = params.get(key);
  if (raw === null) return null;
  const value = DateTime.fromISO(raw);
  return value.isValid ? value.startOf('day') : null;
};

const parseIdsParam = (params: URLSearchParams, key: string) => {
  const raw = params.get(key);
  if (raw === null) return null;
  try {
    return ID_ARRAY_SCHEMA.parse(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const parsePlayoutFilterParams = (params: URLSearchParams): PlayoutFilterParams => ({
  eventTypeId: parseIntParam(params, 'eventType'),
  dateRange: [parseDateParam(params, 'from'), parseDateParam(params, 'to')],
  eventIds: parseIdsParam(params, 'events'),
  deviceGroupIds: parseIdsParam(params, 'deviceGroups'),
  combineByName: params.get('combine') !== 'false',
});

export const serializePlayoutFilterParams = (
  params: URLSearchParams,
  { eventTypeId, dateRange, eventIds, deviceGroupIds, combineByName }: PlayoutFilterParams,
) => {
  const next = new URLSearchParams(params);
  const setOrDelete = (key: string, value: string | null) => {
    if (value === null) next.delete(key);
    else next.set(key, value);
  };
  setOrDelete('eventType', eventTypeId !== null ? String(eventTypeId) : null);
  setOrDelete('from', dateRange[0]?.toISODate() ?? null);
  setOrDelete('to', dateRange[1]?.toISODate() ?? null);
  setOrDelete('events', eventIds !== null ? JSON.stringify(eventIds) : null);
  setOrDelete('deviceGroups', deviceGroupIds !== null ? JSON.stringify(deviceGroupIds) : null);
  setOrDelete('combine', combineByName ? null : 'false');
  next.delete('page');
  return next;
};

export const usePlayoutFilterParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parsePlayoutFilterParams(searchParams), [searchParams]);

  const setFilters = useCallback(
    (patch: Partial<PlayoutFilterParams>, { push = false }: { push?: boolean } = {}) =>
      setSearchParams(
        (params) =>
          serializePlayoutFilterParams(params, { ...parsePlayoutFilterParams(params), ...patch }),
        { replace: !push },
      ),
    [setSearchParams],
  );

  return { filters, setFilters };
};
